import React from "react";
import { FaLinkedin, FaEnvelope } from "react-icons/fa";
import { 
  Target, 
  Code, 
  Award, 
  Users, 
  Lightbulb, 
  Heart, 
  Star,
  Quote,
  Briefcase,
  GraduationCap,
  Calendar,
  Mail,
  Linkedin
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import CEOImg from "../../assets/img/Director.png";

const AboutDirector = () => {
  const qualities = [
    {
      title: "Visionary Leadership",
      icon: Target,
      description:
        "Drives Oethical Technology with strategic vision and innovative thinking, ensuring long-term growth and industry relevance.",
      gradient: "from-blue-500/20 to-cyan-500/20",
    },
    {
      title: "Technical Expertise",
      icon: Code,
      description:
        "With a strong tech background, our CEO guides development teams to deliver scalable and high-performing solutions.",
      gradient: "from-purple-500/20 to-pink-500/20",
    },
    {
      title: "Commitment to Excellence",
      icon: Award,
      description:
        "Leads with a passion for quality and client satisfaction, setting high standards across all areas of Oethical Technology.",
      gradient: "from-orange-500/20 to-red-500/20",
    },
  ];

  const values = [
    {
      title: "Integrity",
      icon: Heart,
      desc: "We uphold honesty and strong moral principles in every decision and action.",
    },
    {
      title: "Innovation",
      icon: Lightbulb,
      desc: "We embrace change and strive to lead with creativity and cutting-edge solutions.",
    },
    {
      title: "Empathy",
      icon: Users,
      desc: "We care deeply about clients, employees, and society, putting people first.",
    },
    {
      title: "Excellence",
      icon: Star,
      desc: "We deliver the highest quality in everything we do, exceeding expectations.",
    },
  ];

  const achievements = [
    { label: "Years Experience", value: "3+", icon: Briefcase },
    { label: "Company Founded", value: "2022", icon: Calendar },
    { label: "Projects Led", value: "500+", icon: Code },
    { label: "Team Members", value: "50+", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-12 md:py-20 lg:py-24 bg-gradient-to-br from-background via-muted/20 to-background border-b">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl blur-3xl -z-10" />
              <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                <img
                  src={CEOImg}
                  alt="Mr. Deepak Yadav - CEO & Director"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6 order-1 lg:order-2">
              <div className="inline-block">
                <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full border border-primary/20">
                  Leadership
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                Meet Our{" "}
                <span className="text-primary bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                  CEO & Director
                </span>
              </h1>

              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-primary mb-2">
                  Mr. Deepak Yadav
                </h2>
                <p className="text-lg text-muted-foreground">
                  Founder & Director, Oethical Technology
                </p>
              </div>

              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                At the heart of <strong className="text-foreground">Oethical Technology</strong> is a
                visionary leader who believes in the power of innovation to
                transform industries. With a deep understanding of technology
                and a passion for creating smart, scalable solutions, our CEO
                has been instrumental in shaping the company's direction.
              </p>

              {/* Social Links */}
              <div className="flex gap-4 pt-4">
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="group"
                >
                  <a
                    href="mailto:deepak.iitn@gmail.com"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Mail className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                    Email
                  </a>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="group"
                >
                  <a
                    href="https://www.linkedin.com/in/deepak-yadav%E2%9C%94-b32339221/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Linkedin className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                    LinkedIn
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                About the{" "}
                <span className="text-primary">Director</span>
              </h2>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
            </div>

            <div className="space-y-6 text-base md:text-lg text-muted-foreground leading-relaxed">
              <p>
                The Director of Oethical Technology brings over three years of
                prior experience in software development, full-stack engineering,
                and IT service management. Since founding the company in October
                2022, he has led its growth with a clear vision for delivering
                high-quality digital solutions across mobile and web platforms.
                His expertise spans iOS and Android development, React.js, and
                project leadership, enabling the team to execute complex projects
                efficiently in a hybrid work environment.
              </p>
              <p>
                In addition to his role at Oethical Technology, he has also been
                serving as the Director of JP News since October 2024, where he
                oversees digital media operations and strategic content delivery.
                His leadership across both ventures reflects a strong commitment
                to innovation, technical excellence, and organizational growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Stats */}
      <section className="py-16 md:py-20 bg-background border-y">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {achievements.map((achievement, index) => {
              const Icon = achievement.icon;
              return (
                <Card key={index} className="text-center border-border hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-3xl md:text-4xl font-bold text-primary mb-2">
                      {achievement.value}
                    </CardTitle>
                    <CardDescription className="text-sm md:text-base">
                      {achievement.label}
                    </CardDescription>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Qualities */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Leadership{" "}
              <span className="text-primary">Qualities</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              At <strong>Oethical Technology</strong>, our CEO leads with
              inspiration, technical brilliance, and a relentless drive for
              excellence — shaping the path we walk every day.
            </p>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4" />
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {qualities.map((quality, index) => {
              const Icon = quality.icon;
              return (
                <Card
                  key={index}
                  className={cn(
                    "border-border hover:border-primary/50 transition-all duration-300 hover:shadow-xl group"
                  )}
                >
                  <CardHeader>
                    <div className={cn(
                      "h-14 w-14 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4 group-hover:scale-110 transition-transform",
                      quality.gradient
                    )}>
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl mb-2">{quality.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed">
                      {quality.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Vision &{" "}
              <span className="text-primary">Values</span>
            </h2>
            <div className="w-16 h-1 bg-primary mx-auto rounded-full" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card
                  key={index}
                  className="border-border hover:border-primary/50 transition-all hover:shadow-lg group"
                >
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl text-primary mb-2">
                      {value.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed">
                      {value.desc}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 md:py-20 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
              <CardContent className="p-8 md:p-12">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Quote className="h-6 w-6 text-primary" />
                  </div>
                  <div className="space-y-4">
                    <blockquote className="text-lg md:text-xl text-foreground italic leading-relaxed">
                      "I believe leadership is not about titles but action and impact.
                      At our company, we put people first – both clients and employees."
                    </blockquote>
                    <div className="flex items-center gap-2 pt-4 border-t border-border/50">
                      <div>
                        <p className="font-semibold text-foreground">Mr. Deepak Yadav</p>
                        <p className="text-sm text-muted-foreground">CEO & Director, Oethical Technology</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Connect Section */}
      <section className="py-16 md:py-20 bg-muted/30">
        <div className="container">
          <div className="max-w-2xl mx-auto text-center space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Connect with the{" "}
                <span className="text-primary">Director</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Reach out to discuss opportunities, partnerships, or simply connect.
              </p>
              <div className="w-16 h-1 bg-primary mx-auto rounded-full mt-4" />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="outline"
                asChild
                className="group"
              >
                <a
                  href="mailto:deepak.iitn@gmail.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Mail className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                  Send Email
                </a>
              </Button>
              <Button
                size="lg"
                asChild
                className="group"
              >
                <a
                  href="https://www.linkedin.com/in/deepak-yadav%E2%9C%94-b32339221/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutDirector;
